import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { IIncidentCode, IncidentCodeService } from '@shared/services/incident-code-service';
import { NotificationService } from '@shared/services/notification.service';
import { map, Subject, takeUntil } from 'rxjs';
import { CreateIncidentCodeComponent } from './create-incident-code/create-incident-code.component';
import { DeleteIncidentCodeComponent } from './delete-incident-code/delete-incident-code.component';
import { UpdateIncidentCodeComponent } from './update-incident-code/update-incident-code.component';

interface IIncidentCodeView {
	alias: string;
	name: string;
	group: string;
	isActive: boolean;
	incidentCode: IIncidentCode;
}

@Component({
	selector: 'app-incident-code',
	templateUrl: './incident-code.component.html',
	styleUrl: './incident-code.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class IncidentCodeComponent {
	private destroy$: Subject<void> = new Subject<void>();
	displayedColumns: string[] = ['name', 'group', 'isActive', 'menu'];
	dataSource = new MatTableDataSource<IIncidentCodeView>();
	isLoading = false;
	showInactive = false;

	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild(MatSort) sort: MatSort;

	favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_add',
			text: () => 'mes.incident-code.buttons.createbtn',
			click: () => this.openCreate(),
		},
	];

	constructor(
		public dialog: MatDialog,
		private notificationService: NotificationService,
		private service: IncidentCodeService,
	) {
		this.dataSource.sortData = (data, sort) => {
			const isAsc = sort.direction === 'asc';

			return data.sort((a, b) => {
				const valueA = (a[sort.active] ?? '').toString().toLowerCase();
				const valueB = (b[sort.active] ?? '').toString().toLowerCase();

				if (valueA < valueB) return isAsc ? -1 : 1;
				if (valueA > valueB) return isAsc ? 1 : -1;
				return 0;
			});
		};
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	ngAfterViewInit() {
		this.dataSource.paginator = this.paginator;
		this.dataSource.sort = this.sort;
		this.updateList();
	}

	private getTableViewModel(incidentCodes: IIncidentCode[]): IIncidentCodeView[] {
		return (
			incidentCodes?.map((incidentCode) => {
				return {
					alias: incidentCode.alias,
					name: incidentCode.name,
					group: incidentCode.group,
					isActive: incidentCode.isActive,
					incidentCode,
				};
			}) ?? []
		);
	}

	updateList() {
		if (this.isLoading) {
			return;
		}

		this.isLoading = true;
		this.service
			.getAll()
			.pipe(map((incidentCode) => (this.showInactive ? incidentCode : incidentCode.filter((ic) => ic.isActive))))
			.subscribe((incidentCode) => {
				this.dataSource.data = this.getTableViewModel(incidentCode);
				this.dataSource.sort = this.sort;
				this.onSort(this.sort);
				this.isLoading = false;
			});
	}

	updateListItem(incidentCode: IIncidentCode) {
		if (!incidentCode) {
			return;
		}
		const item = this.getTableViewModel([incidentCode])[0];
		const idx = this.dataSource.data.findIndex((x) => x.incidentCode.id === item.incidentCode.id);
		if (idx > -1) {
			this.dataSource.data[idx] = item;
			this.dataSource.data = [...this.dataSource.data];
		}
	}

	addToList(incidentCode: IIncidentCode) {
		const item = this.getTableViewModel([incidentCode])[0];
		if (this.showInactive || item.isActive) {
			this.dataSource.data.push(item);
			this.dataSource.data = [...this.dataSource.data];
		}
	}

	get groupNames(): string[] {
		return [...new Set(this.dataSource.data.map((c) => c.group).filter(Boolean))].sort();
	}

	removeFromList(id: string): void {
		if (!id || !this.dataSource?.data?.length) {
			return;
		}
		this.dataSource.data = this.dataSource.data.filter((x) => x.incidentCode.id !== id);
	}

	public onSort(sort: Sort) {
		if (sort.direction === '') {
			this.sort.sort({ id: 'alias', start: 'asc', disableClear: true });
		}
	}

	applyFilter(event: KeyboardEvent) {
		if (event.target instanceof HTMLInputElement) {
			const filterValue = event.target.value;
			this.dataSource.filter = filterValue.trim().toLowerCase();
		}
	}

	toggleShowInactive() {
		this.showInactive = !this.showInactive;
		this.updateList();
	}

	onToggleActive(incidentCode: IIncidentCode) {
		incidentCode.isActive = !incidentCode.isActive;
		this.service.update(incidentCode).subscribe(() => {
			this.updateListItem(incidentCode);
		});
	}

	openCreate() {
		const dialogRef = this.dialog.open(CreateIncidentCodeComponent, {
			disableClose: true,
			width: '450px',
			data: { incidentCodeGroups: this.groupNames },
		});

		dialogRef.componentInstance.created.pipe(takeUntil(this.destroy$)).subscribe((response) => {
			this.addToList(response.result);
			this.notificationService.notify(['mes.incident-code.notifications.create.header'], ['mes.incident-code.notifications.create.content', { alias: response.result.alias }], 'success');
		});
	}

	openEdit(incidentCode: IIncidentCode | null) {
		const dialogRef = this.dialog.open(UpdateIncidentCodeComponent, {
			disableClose: true,
			width: '450px',
			data: { incidentCode, incidentCodeGroups: this.groupNames },
		});

		dialogRef.componentInstance.updated.pipe(takeUntil(this.destroy$)).subscribe((response) => {
			this.updateListItem(response.result);
			this.notificationService.notify(['mes.incident-code.notifications.update.header'], ['mes.incident-code.notifications.update.content', { alias: response.result.alias }], 'success');
		});
	}

	openDelete(incidentCode: IIncidentCode) {
		const dialogRef = this.dialog.open(DeleteIncidentCodeComponent, {
			disableClose: true,
			width: '450px',
			data: incidentCode,
		});
		dialogRef.componentInstance.deleted.pipe(takeUntil(this.destroy$)).subscribe((id) => {
			this.removeFromList(id);
			this.notificationService.notify(['mes.incident-code.notifications.delete.header'], ['mes.incident-code.notifications.delete.content', { alias: incidentCode.alias }], 'success');
		});
	}
}
